import React, { useState, useEffect, useCallback } from "react";
import { Bot, Loader2, CheckCircle2, XCircle, PhoneOff } from "lucide-react";
import { initiateAICall, getAICallStatus } from "../api/aiDialer";

/**
 * AICallButton - Small inline button that initiates AI calls
 * Shows a spinner while call is in progress, then updates notes when done
 */
const AICallButton = ({ lead, onCallComplete, userCredits = 0, creditsPerCall = 250 }) => {
  const [status, setStatus] = useState("idle"); // idle, initiating, calling, in_progress, completed, failed, no_answer
  const [callId, setCallId] = useState(null);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  const [pollCount, setPollCount] = useState(0);

  const MAX_POLLS = 60; // Stop after ~3 minutes (60 * 3 seconds)

  // Get lead info
  const leadId = lead.lead_id || lead.journey_id || lead.leadId || lead._id || lead.id;
  const leadPhone = lead.borrower_phone || lead.contact_phone;
  const hasEnoughCredits = userCredits >= creditsPerCall;

  // Poll for call status
  const pollStatus = useCallback(async () => {
    if (!callId) return;

    try {
      const statusData = await getAICallStatus(callId);

      // If null returned (404), call record may not exist yet or was deleted
      if (!statusData) {
        console.log("Call status returned null, may still be initializing...");
        return;
      }

      setStatus(statusData.status);

      // Call completed - notify parent to refresh notes
      if (["completed", "failed", "no_answer", "voicemail"].includes(statusData.status)) {
        setResult(statusData.result);
        if (onCallComplete) {
          onCallComplete(statusData);
        }
        // Reset after 3 seconds
        setTimeout(() => {
          setStatus("idle");
          setCallId(null);
          setResult(null);
          setPollCount(0);
        }, 3000);
        return;
      }
    } catch (err) {
      console.error("Error polling call status:", err);
    }
  }, [callId, onCallComplete]);

  // Poll every 3 seconds while call is active
  useEffect(() => {
    if (!callId || ["idle", "completed", "failed", "no_answer", "voicemail"].includes(status)) {
      return;
    }

    // Stop polling after max attempts
    if (pollCount >= MAX_POLLS) {
      console.log("Max poll attempts reached, stopping");
      setStatus("failed");
      setError("Call timed out");
      setTimeout(() => {
        setStatus("idle");
        setCallId(null);
        setError(null);
        setPollCount(0);
      }, 3000);
      return;
    }

    const timeout = setTimeout(() => {
      pollStatus();
      setPollCount(c => c + 1);
    }, 3000);

    return () => clearTimeout(timeout);
  }, [callId, status, pollStatus, pollCount]);

  const handleClick = async () => {
    if (!leadPhone) {
      setError("No phone number");
      setTimeout(() => setError(null), 2000);
      return;
    }

    if (!leadId) {
      setError("No lead ID");
      setTimeout(() => setError(null), 2000);
      return;
    }

    if (!hasEnoughCredits) {
      setError("Need more credits");
      setTimeout(() => setError(null), 2000);
      return;
    }

    setStatus("initiating");
    setError(null);
    setPollCount(0);

    try {
      const response = await initiateAICall({
        lead_id: leadId,
        custom_context: null,
        preferred_callback_slots: null
      });

      setCallId(response.call_id);
      setStatus(response.status || "calling");
    } catch (err) {
      setError(err.message || "Failed");
      setStatus("idle");
      setTimeout(() => setError(null), 3000);
    }
  };

  // Idle state - show AI Call button
  if (status === "idle") {
    return (
      <button
        onClick={handleClick}
        disabled={!leadPhone || !hasEnoughCredits}
        className="px-2 py-1 bg-violet-500 hover:bg-violet-600 disabled:bg-violet-300 disabled:cursor-not-allowed text-white text-xs rounded-lg transition-colors flex items-center gap-1"
        title={!hasEnoughCredits ? `Need ${creditsPerCall} credits` : "AI will call and qualify this lead"}
      >
        <Bot className="w-3 h-3" />
        {error || "AI Call"}
      </button>
    );
  }

  // Initiating / Calling / In Progress - show spinner
  if (["initiating", "calling", "queued", "in_progress"].includes(status)) {
    return (
      <div className="px-2 py-1 bg-violet-600 text-white text-xs rounded-lg flex items-center gap-1.5 animate-pulse">
        <Loader2 className="w-3 h-3 animate-spin" />
        <span>
          {status === "initiating" && "Starting..."}
          {status === "queued" && "Queued..."}
          {status === "calling" && "Calling..."}
          {status === "in_progress" && "In Call..."}
        </span>
      </div>
    );
  }

  // Completed - show brief result
  if (status === "completed") {
    const interested = result?.interested;
    return (
      <div className={`px-2 py-1 text-white text-xs rounded-lg flex items-center gap-1 ${
        interested ? "bg-emerald-500" : "bg-slate-500"
      }`}>
        {interested ? <CheckCircle2 className="w-3 h-3" /> : <XCircle className="w-3 h-3" />}
        <span>{interested ? "Interested!" : "Not interested"}</span>
      </div>
    );
  }

  // Failed / No Answer
  if (["failed", "no_answer", "voicemail"].includes(status)) {
    return (
      <div className="px-2 py-1 bg-red-500 text-white text-xs rounded-lg flex items-center gap-1">
        <PhoneOff className="w-3 h-3" />
        <span>
          {status === "no_answer" && "No answer"}
          {status === "voicemail" && "Voicemail"}
          {status === "failed" && "Failed"}
        </span>
      </div>
    );
  }

  return null;
};

export default AICallButton;
